import * as React from 'react'
import { ALL_VENUES } from '@/lib/bff/today'

/*
 * Holds the Today venue scope per tenant. Unlike the tenant/language
 * selections (session-scoped), the venue scope persists per user across
 * sessions and devices-of-this-browser (VIS-257), so it lives in localStorage
 * keyed by tenant. Every Today module reads the active venue from here, so the
 * whole dashboard stays scoped to one venue. Defaults to the `all` sentinel.
 */

type VenueScopeContextValue = {
  getVenue: (tenantId: string) => string
  setVenue: (tenantId: string, venueId: string) => void
}

const VenueScopeContext = React.createContext<VenueScopeContextValue | null>(null)
const STORAGE_KEY = 'visitone.todayVenueScope'

export function VenueScopeProvider({ children }: { children: React.ReactNode }) {
  const [byTenant, setByTenant] = React.useState<Record<string, string>>({})

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) setByTenant(JSON.parse(raw) as Record<string, string>)
    } catch {
      // Ignore unavailable or malformed local storage.
    }
  }, [])

  const setVenue = React.useCallback((tenantId: string, venueId: string) => {
    setByTenant((prev) => {
      const next = { ...prev, [tenantId]: venueId }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        // Non-fatal: selection still applies for the current in-memory session.
      }
      return next
    })
  }, [])

  const value = React.useMemo<VenueScopeContextValue>(
    () => ({
      getVenue: (tenantId: string) => byTenant[tenantId] ?? ALL_VENUES,
      setVenue,
    }),
    [byTenant, setVenue],
  )

  return <VenueScopeContext.Provider value={value}>{children}</VenueScopeContext.Provider>
}

export function useVenueScope() {
  const context = React.useContext(VenueScopeContext)
  if (!context) throw new Error('useVenueScope must be used within a VenueScopeProvider')
  return context
}
